import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { imageForSchool } from "@/lib/helpers";
import { Trophy, MapPin, ChevronRight } from "lucide-react";
import { setPageMeta, injectJsonLd, removeJsonLd } from "@/lib/seo";
import { cityTopSchema } from "@/lib/schema";

export default function TopByCity() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [allSchools, setAllSchools] = useState([]);

  useEffect(() => {
    setData(null); setError(null); setAllSchools([]);
    api.get(`/cities/by-slug/${slug}/top`, { params: { limit: 10 } })
      .then(({ data }) => {
        setData(data);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Errore"));
    // Fetch ALL schools of the city (for SEO/internal linking + user discovery)
    api.get(`/cities/by-slug/${slug}/all-schools`)
      .then(({ data }) => setAllSchools(data.items || []))
      .catch(() => setAllSchools([]));
  }, [slug]);

  // SEO meta + JSON-LD (invisibili all'utente)
  useEffect(() => {
    if (!data) return;
    setPageMeta({
      title: `Le migliori scuole dell'infanzia a ${data.city} — Per manina`,
      description: `Classifica delle migliori scuole dell'infanzia a ${data.city} (${data.province}) secondo le esperienze dei genitori. ${data.total_schools} scuole monitorate.`.slice(0, 250),
      canonicalPath: `/migliori/${slug}`,
      ogType: "website",
    });
    injectJsonLd("schema-city", cityTopSchema(data.city, data.region, data.province, slug, [...(data.items || []), ...allSchools]));
    return () => removeJsonLd("schema-city");
  }, [data, allSchools, slug]);

  if (error) {
    return <div className="max-w-3xl mx-auto px-4 py-20 font-ui text-[#5C6B64]">{error}</div>;
  }
  if (!data) {
    return <div className="max-w-3xl mx-auto px-4 py-20 font-ui text-[#5C6B64]">Caricamento…</div>;
  }

  const list = data.items || [];
  const fillers = data.more_unreviewed || [];

  return (
    <div data-testid={`top-city-${slug}`}>
      {/* Header */}
      <section className="bg-[#F2EFEB] border-b border-[#E8E6E1]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-14">
          <nav className="font-ui text-xs text-[#5C6B64] flex items-center gap-1 mb-3 sm:mb-4">
            <Link to="/" className="hover:text-[#C05C45]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/migliori" className="hover:text-[#C05C45]">Migliori scuole</Link>
            <ChevronRight size={12} />
            <span className="text-[#1A362D]">{data.city}</span>
          </nav>
          <div className="flex items-start gap-2.5 sm:gap-3">
            <Trophy className="text-[#E19A32] mt-0.5 sm:mt-1 flex-shrink-0" size={22} />
            <div>
              <h1 className="font-display text-2xl sm:text-4xl lg:text-5xl leading-tight tracking-tight text-[#1A362D]">
                Le 10 scuole dell'infanzia più amate a {data.city}
              </h1>
              <p className="mt-2 sm:mt-3 font-ui text-sm sm:text-base text-[#5C6B64] max-w-2xl">
                Classifica aggiornata in tempo reale sulla base delle esperienze dei genitori della community.
                {" "}
                <span className="font-ui text-[#1A362D]">{data.total_schools.toLocaleString("it-IT")}</span> scuole dell'infanzia censite a {data.city} ({data.province}).
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <main className="lg:col-span-8 space-y-3" data-testid="top-list">
          {list.length === 0 && (
            <div className="card-surface p-10 text-center">
              <p className="font-display text-2xl text-[#1A362D]">La classifica sta per nascere</p>
              <p className="mt-2 font-ui text-sm text-[#5C6B64]">Le scuole di {data.city} non hanno ancora esperienze. Sei un genitore? <Link to="/cerca" className="underline text-[#C05C45]">Racconta la tua per primo</Link>.</p>
            </div>
          )}

          {list.map((s, i) => (
            <Link
              key={s.id}
              to={`/scuola/${s.id}`}
              data-testid={`top-school-${i+1}`}
              className="card-surface flex items-stretch gap-5 hover:border-[#C05C45] transition-colors overflow-hidden group"
            >
              <div className="flex items-center justify-center w-16 sm:w-20 bg-[#F2EFEB] border-r border-[#E8E6E1]">
                <span className="font-display text-4xl sm:text-5xl text-[#C05C45]">{i + 1}</span>
              </div>
              <img src={imageForSchool(s.id)} alt="" className="hidden sm:block w-32 object-cover" />
              <div className="flex-1 py-4 pr-5 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`label-eyebrow ${s.type === "pubblica" ? "text-[#1A362D]" : "text-[#C05C45]"}`}>
                    {s.type === "pubblica" ? "Pubblica" : "Paritaria/Privata"}
                  </span>
                </div>
                <h2 className="font-display text-xl sm:text-2xl text-[#1A362D] leading-tight group-hover:text-[#C05C45] transition-colors">{s.name}</h2>
                <p className="font-ui text-xs text-[#5C6B64] mt-1 flex items-start gap-1"><MapPin size={11} className="mt-0.5" /> {s.address}</p>
                <div className="mt-2">
                  {s.review_count > 0 ? (
                    <span
                      data-testid={`top-school-reviews-${s.id}`}
                      className="inline-flex items-center gap-1.5 bg-[#DCE4DC] text-[#1A362D] rounded-full px-3 py-1 font-ui text-xs font-semibold"
                    >
                      💬 {s.review_count} {s.review_count === 1 ? "recensione" : "recensioni"}
                    </span>
                  ) : (
                    <span className="font-ui text-xs text-[#5C6B64] italic">Ancora nessuna recensione</span>
                  )}
                </div>
              </div>
            </Link>
          ))}

          {fillers.length > 0 && allSchools.length === 0 && (
            <div className="mt-10">
              <h3 className="font-display text-2xl text-[#1A362D]">Altre scuole a {data.city}</h3>
              <p className="font-ui text-xs text-[#8A9691] mt-1">Ancora senza esperienze — aspettano i primi genitori.</p>
              <div className="mt-4 space-y-2">
                {fillers.map((s) => (
                  <Link key={s.id} to={`/scuola/${s.id}`} className="block card-surface p-4 hover:border-[#C05C45] transition-colors">
                    <p className="font-display text-lg text-[#1A362D]">{s.name}</p>
                    <p className="font-ui text-xs text-[#5C6B64] mt-0.5">{s.address}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Elenco completo alfabetico di tutte le scuole del comune — genera link interni per SEO */}
          {allSchools.length > 0 && (
            <div className="mt-14" data-testid="city-all-schools">
              <h2 className="font-display text-3xl text-[#1A362D]">
                Elenco completo delle {allSchools.length} scuole dell'infanzia a {data.city}
              </h2>
              <p className="mt-2 font-ui text-sm text-[#5C6B64] max-w-2xl">
                Ordinato alfabeticamente. Clicca su una scheda per leggere le esperienze, valutare la struttura o segnalare una correzione.
              </p>
              <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 border-t border-[#E8E6E1] pt-6">
                {allSchools.map((s) => (
                  <li key={s.id} className="border-b border-dashed border-[#E8E6E1] py-2">
                    <Link
                      to={`/scuola/${s.id}`}
                      data-testid={`city-school-${s.id}`}
                      className="group flex items-baseline gap-2 hover:text-[#C05C45]"
                    >
                      <span className="font-ui text-[15px] text-[#1A362D] group-hover:text-[#C05C45] transition-colors leading-snug">
                        {s.name}
                      </span>
                      {s.review_count > 0 && (
                        <span className="text-[10px] font-ui text-[#8A9691] whitespace-nowrap">
                          💬 {s.review_count} {s.review_count === 1 ? "recensione" : "recensioni"}
                        </span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </main>

        {/* SEO / promotional aside */}
        <aside className="lg:col-span-4">
          <div className="card-surface p-6 sticky top-24">
            <p className="label-eyebrow text-[#C05C45]">Perché Per manina</p>
            <h3 className="mt-1 font-display text-2xl text-[#1A362D]">Esperienze reali da genitori veri</h3>
            <p className="mt-3 font-ui text-sm text-[#5C6B64] leading-relaxed">
              Le esperienze sono lasciate da genitori autenticati con Google. Niente bot, niente pubblicità.
            </p>
            <Link to="/cerca" className="mt-5 block btn-primary text-center text-sm">Cerca un'altra città</Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
